// SymmetryCircleTask.js
import { InlineMath } from "react-katex";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import { CIRCLE_GRAPH_COLORS, getCircleGraphRanges } from "../../../../components/GraphPlotter/circleGraphRanges";
import EquationInput from "../../../../components/inputs/EquationInput";
import { checkCircleEquation } from "../../../../utils/circleEquation";

function formatCoord(v, variable) {
  if (v === 0) return `${variable}^2`;
  if (v > 0) return `(${variable} - ${v})^2`;
  return `(${variable} + ${Math.abs(v)})^2`;
}

const symmetries = [
  { label: "OX", reflect: ([x, y]) => [x, -y] },
  { label: "OY", reflect: ([x, y]) => [-x, y] },
  { label: "środek układu", reflect: ([x, y]) => [-x, -y] },
];

export function generate() {
  const symmetry = symmetries[Math.floor(Math.random() * symmetries.length)];
  let cx;
  let cy;
  let cxr;
  let cyr;

  // Środek leżący na osi odbicia nie daje widocznej zmiany na rysunku.
  do {
    cx = Math.floor(Math.random() * 11) - 5;
    cy = Math.floor(Math.random() * 11) - 5;
    [cxr, cyr] = symmetry.reflect([cx, cy]);
  } while (cx === cxr && cy === cyr);

  const r = Math.floor(Math.random() * 3) + 2;

  return {
    type: "SymmetryCircleTask",
    data: {
      center: { x: cx, y: cy },
      radius: r,
      symmetryLabel: symmetry.label,
    },
    answer: { x: cxr, y: cyr },
  };
}

// Zgodność z runnerem przygotowania do ścieżki maturalnej.
export function generateTask() {
  return generate();
}


export function renderTask(task) {
  const { center, radius, symmetryLabel } = task.data;

  return (
    <p>
      Wyznacz <strong>równanie okręgu</strong> będącego obrazem okręgu o środku{" "}
      <InlineMath math={`S = (${center.x}, ${center.y})`} /> i promieniu{" "}
      <InlineMath math={`r~=~${radius}`} /> względem{" "}
      <strong>
        {symmetryLabel === "OX" || symmetryLabel === "OY" ? (
          <>osi <InlineMath math={`\\mathrm{${symmetryLabel}}`} /></>
        ) : (
          "środka układu"
        )}
      </strong>.
    </p>
  );
}


export function renderAnswer(task) {
  const { radius } = task.data;
  const { x, y } = task.answer;

  const xExpr = formatCoord(x, "x");
  const yExpr = formatCoord(y, "y");
  const rSquared = radius ** 2;

  return (
    <>
      <p>
        Obrazem jest okrąg o środku{" "}
        <InlineMath math={`S' = (${x}, ${y})`} /> i promieniu{" "}
        <InlineMath math={`r = ${radius}`} />.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        Równanie tego okręgu to:{" "}
        <InlineMath math={`${xExpr} + ${yExpr} = ${rSquared}`} />
      </p>
    </>
  );
}


export function renderExplanation({ data, answer }) {
  const { center, radius, symmetryLabel } = data;
  const { xRange, yRange, graphSize } = getCircleGraphRanges({
    circles: [{ center, radius }, { center: answer, radius }],
    points: [center, answer],
  });

  return (
    <div>
      <p>
        Odbicie względem{" "}
        <strong>
          {symmetryLabel === "OX" || symmetryLabel === "OY" ? (
            <>osi <InlineMath math={`\\mathrm{${symmetryLabel}}`} /></>
          ) : (
            "środka układu"
          )}
        </strong>{" "}
        zmienia położenie środka okręgu, ale promień pozostaje bez zmian.
      </p>
      <div style={{ display: "flex", justifyContent: "center", marginTop: "1.5rem" }}>
        <GraphViewer
          points={{
            S: { ...center, color: CIRCLE_GRAPH_COLORS.point, textColor: CIRCLE_GRAPH_COLORS.point },
            "S'": { ...answer, color: CIRCLE_GRAPH_COLORS.point, textColor: CIRCLE_GRAPH_COLORS.point },
          }}
          circles={[
            { center, radius, color: CIRCLE_GRAPH_COLORS.circle },
            { center: answer, radius, color: CIRCLE_GRAPH_COLORS.circle },
          ]}
          xRange={xRange}
          yRange={yRange}
          width={graphSize}
          height={graphSize}
        />
      </div>
    </div>
  );
}


export function renderInput(task, userInput, setUserInput, disabled) {
  return (
    <EquationInput
      value={userInput}
      onChange={setUserInput}
      disabled={disabled}
    />
  );
}

export function checkAnswer(task, userInput) {
  return checkCircleEquation(userInput, {
    centerX: task.answer.x, centerY: task.answer.y, rSquared: task.data.radius ** 2,
  });
}


export function renderUserAnswer(userInput) {
  if (!userInput || userInput.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: "0.5rem" }}>
    <InlineMath math={userInput} />
    </div>
  );
}


